import { useContext, useEffect, useState } from "react";
import {
  getMappedFields,
  getProductFields,
  createProductFields,
  updateProductFields,
  deleteProductFields,
} from "../services/configurations/fields";
import { updateProductProperty } from "../services/configurations/products";
import { MappedField, ProductFieldData } from "../interfaces";
import { AppContext } from "../context";

type FieldType = "PROPERTY" | "FIELD";
type ActionType = "CREATE" | "UPDATE" | "DELETE";

interface FieldsData {
  mappedFieldData: MappedField[];
  productFieldData: ProductFieldData[];
}

const fieldsDataDefaultValues = {
  mappedFieldData: [],
  productFieldData: []
}

const handleProductFieldActionFunction = (action: ActionType, t: Function) => {
  switch (action) {
    case "CREATE":
      return {
        actionFn: createProductFields,
        fnMsg: t("Products.productFieldApiCreateLabel"),
      };
    case "UPDATE":
      return {
        actionFn: updateProductFields,
        fnMsg: t("Products.productFieldApiUpdateLabel"),
      };
    case "DELETE":
      return {
        actionFn: deleteProductFields,
        fnMsg: t("Products.productFieldApiDeleteLabel"),
      };
    default:
      return {
        actionFn: () => {},
        fnMsg: "",
      };
  }
};

const handleProductPropertyActionFunction = (
  action: ActionType,
  t: Function
) => {
  switch (action) {
    case "UPDATE":
      return {
        actionFn: updateProductProperty,
        fnMsg: t("Products.productPropertyApiUpdateLabel"),
      };
    default:
      return {
        actionFn: () => {},
        fnMsg: "",
      };
  }
};

export const useProductFields = (
  usageType?: string,
  serviceApi?: string,
  dashboardOption?: boolean
) => {
  const {
    t,
    toast,
    appState: { selectedApp, selectedProduct },
    setAppState,
  } = useContext(AppContext);
  const [data, setData] = useState<FieldsData | null>(null);
  const [filteredData, setFilteredData] = useState<ProductFieldData[] | null>(null);
  const [selectedFieldsIds, setSelectedFieldsIds] = useState<Array<number>>([]);

  const [reload, setReload] = useState<number>(0);
  const appid = selectedApp?.appid || '0';

  const reloadFn = () => {
    setData(fieldsDataDefaultValues);
    setFilteredData(null)
    setReload((prev) => prev + 1);
  };

  const productFieldAction = async (
    field: FieldType,
    action: ActionType,
    data: any
  ) => {
    const { actionFn, fnMsg } =
      field === "FIELD"
        ? handleProductFieldActionFunction(action, t)
        : handleProductPropertyActionFunction(action, t);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    const actionStatus = await Promise.resolve(actionFn(data, appid))
      .then((response) => {
        toast.current.replace({
          severity: "success",
          summary: "",
          detail: `${fnMsg}`,
          life: 3000,
        });
        return "SUCCESS";
      })
      .catch((error) => {
        toast.current.replace({
          severity: "error",
          summary: "",
          detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
          life: 3000,
        });
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));
    return actionStatus;
  };

  useEffect(() => {
    if (usageType && serviceApi && appid) {
      (async function () {
        await Promise.all([
          getMappedFields(appid, serviceApi, dashboardOption),
          getProductFields(appid, selectedProduct?.id || 0, usageType),
        ])
          .then((response) => {
            const mappedFields: MappedField[] = response[0]?.data;
            const productFields: ProductFieldData[] = response[1]?.data;
            setData({
              mappedFieldData: mappedFields,
              productFieldData: productFields,
            });
            setFilteredData(productFields)
            setSelectedFieldsIds(productFields.map(field => field.mappedfieldid))
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [
    appid,
    toast,
    selectedProduct,
    reload,
    usageType,
    serviceApi,
    dashboardOption,
  ]);

  return {
    mappedFields: data?.mappedFieldData,
    productFields: data?.productFieldData,
    filteredData,
    setFilteredData,
    selectedFieldsIds,
    productFieldAction,
    reloadFn,
  };
};
